import { getWidgetApiClient } from './api';
import { createConversationStore } from './storage';
import type { ChatSessionState } from './chat-session';
import type { ChatMessage, ConversationStore, MessageEntry, WidgetApiClient } from './types';
import {
  appendEntry,
  createAssistantMessage,
  createUserMessage,
  isConversationNotFoundError,
  replaceEntriesForRetry,
} from './chat-utils';
import { createPoller, type Poller } from './poller';

export interface ChatController {
  initialize: () => Promise<void>;
  send: (text: string) => void;
  clear: () => void;
  retry: () => void;
  /** Record visitor interaction to keep the poll loop active. */
  recordInteraction: () => void;
  /** Start the poll loop (call when the chat panel opens). */
  startPolling: () => void;
  /** Stop the poll loop (call when the chat panel closes). */
  stopPolling: () => void;
}

interface ChatControllerOptions {
  siteId: string;
  state: ChatSessionState;
  pageUrl: string;
  getTurnstileToken?: () => Promise<string>;
  forceChallenge?: () => Promise<string>;
  apiClient?: WidgetApiClient;
  conversationStore?: ConversationStore;
}

export function createChatController({
  siteId,
  state,
  pageUrl,
  getTurnstileToken,
  forceChallenge,
  apiClient = getWidgetApiClient(siteId),
  conversationStore = createConversationStore(),
}: ChatControllerOptions): ChatController {
  let poller: Poller | null = null;

  /** Build or rebuild the poller for the current conversationId + token. */
  function buildPoller(): void {
    const conversationId = state.conversationId.value;
    const token = state.token.value;
    if (!conversationId || !token) return;

    if (poller) poller.stop();

    // Collect all IDs present in the current message list for dedup
    const knownIds = new Set<string>(
      state.messages.value
        .filter((e): e is MessageEntry & { kind: 'message' } => e.kind === 'message')
        .flatMap((e) => (e.data.id ? [e.data.id] : [])),
    );

    poller = createPoller(siteId, conversationId, token, apiClient, {
      onNewMessages: (messages: ChatMessage[]) => {
        // Record every arrived id so the dedup set + cursor keep advancing...
        for (const m of messages) {
          if (m.id) knownIds.add(m.id);
        }
        // ...but never re-render the visitor's OWN messages. They're always shown
        // optimistically on send; the optimistic copy has no server id, so a
        // polled-back user message would otherwise appear as a duplicate bubble.
        // The poll only needs to deliver assistant / merchant replies.
        const renderable = messages.filter((m) => m.role !== 'user');
        if (renderable.length === 0) return;
        const newEntries: MessageEntry[] = renderable.map((m) => ({ kind: 'message', data: m }));
        state.messages.value = [...state.messages.value, ...newEntries];
        if (state.conversationId.value) {
          conversationStore.save(siteId, state.conversationId.value, state.messages.value);
        }
      },
      onControllerChange: (controller) => {
        state.controller.value = controller;
      },
      getKnownMessageIds: () => knownIds,
      getLastMessageId: () => {
        const msgs = state.messages.value
          .filter((e): e is MessageEntry & { kind: 'message' } => e.kind === 'message')
          .map((e) => e.data);
        for (let i = msgs.length - 1; i >= 0; i--) {
          if (msgs[i].id) return msgs[i].id!;
        }
        return null;
      },
      getController: () => state.controller.value,
    });

    if (state.isOpen.value) {
      poller.start();
    }
  }

  const initialize = async (): Promise<void> => {
    const { config, token } = await apiClient.fetchConfig(siteId);
    state.config.value = config;
    state.token.value = token;
    state.isLoading.value = false;

    const stored = conversationStore.load(siteId);
    if (stored) {
      state.conversationId.value = stored.id;
      state.messages.value = stored.messages;
      buildPoller();
    }
  };

  const send = (rawText: string): void => {
    const trimmed = rawText.trim();
    if (!trimmed || state.isSending.value) return;

    poller?.recordInteraction();

    const userMsg = createUserMessage(trimmed);
    const userEntry: MessageEntry = { kind: 'message', data: userMsg };
    state.messages.value = appendEntry(state.messages.value, userEntry);
    state.isSending.value = true;
    state.currentStreamContent.value = '';
    state.error.value = null;

    const doSend = async (conversationId: string | null, isRetry = false, providedToken?: string): Promise<void> => {
      function abortSend(msg: string): void {
        state.isSending.value = false;
        state.currentStreamContent.value = '';
        state.error.value = msg;
      }

      function clearStream(): void {
        state.isSending.value = false;
        state.currentStreamContent.value = '';
        state.lastAssistantMessageId.value = null;
      }

      let turnstileToken: string | null = providedToken ?? null;
      if (turnstileToken === null && getTurnstileToken) {
        try {
          turnstileToken = await getTurnstileToken();
        } catch {
          abortSend('Verification failed. Please try again.');
          return;
        }
      }

      // Track the message_id assigned to the streamed assistant reply for dedup with poll
      let streamedMessageId: string | null = null;

      apiClient.sendMessage(
        siteId,
        state.token.value,
        trimmed,
        conversationId,
        pageUrl,
        turnstileToken,
        {
          onConversationId: (id) => {
            const isNew = state.conversationId.value !== id;
            state.conversationId.value = id;
            if (isNew) {
              buildPoller();
            }
          },
          onToken: (token) => {
            state.currentStreamContent.value += token;
          },
          onMessageId: (id) => {
            streamedMessageId = id;
            state.lastAssistantMessageId.value = id;
          },
          onHandoff: () => {
            // Merchant has taken over; switch to fast-poll mode but don't render
            // an assistant bubble — the SSE stream terminates here.
            state.controller.value = 'merchant';
            clearStream();
            // Rebuild poller so it uses the new controller cadence immediately
            if (state.conversationId.value) buildPoller();
          },
          onDone: () => {
            const assistantMsg = createAssistantMessage(
              state.currentStreamContent.value,
              state.lastAssistantMessageId.value,
            );

            // Register the streamed message id in the poller's known set by
            // rebuilding it — the set is captured at buildPoller() time from
            // the message list which now includes the assistant message.
            state.messages.value = appendEntry(state.messages.value, { kind: 'message', data: assistantMsg });

            clearStream();

            if (state.conversationId.value) {
              conversationStore.save(siteId, state.conversationId.value, state.messages.value);
            }

            // If the stream delivered a message_id, register it so the poll
            // loop won't re-deliver the same message.
            if (streamedMessageId && poller) {
              // Re-materialise the poller so the knownIds set includes this new id
              buildPoller();
            }
          },
          onError: (error) => {
            if (isConversationNotFoundError(error) && !isRetry) {
              conversationStore.clear(siteId);
              state.conversationId.value = null;
              state.messages.value = replaceEntriesForRetry(userMsg);
              void doSend(null, true);
              return;
            }
            abortSend(
              isConversationNotFoundError(error)
                ? 'Something went wrong. Please try again.'
                : 'Hmm, having trouble connecting. Try again?'
            );
          },
          onTurnstileRequired: () => {
            if (isRetry || !forceChallenge) {
              abortSend('Verification required. Please try again.');
              return;
            }
            forceChallenge()
              .then((token) => { void doSend(conversationId, true, token); })
              .catch(() => { abortSend('Verification failed. Please try again.'); });
          },
        },
      );
    };

    void doSend(state.conversationId.value);
  };

  const clear = (): void => {
    poller?.stop();
    poller = null;
    conversationStore.clear(siteId);
    state.messages.value = [];
    state.conversationId.value = null;
    state.error.value = null;
    state.controller.value = 'ai';
  };

  const retry = (): void => {
    state.error.value = null;
  };

  const recordInteraction = (): void => {
    poller?.recordInteraction();
  };

  const startPolling = (): void => {
    if (!poller && state.conversationId.value) {
      buildPoller();
    }
    poller?.start();
  };

  const stopPolling = (): void => {
    poller?.stop();
  };

  return {
    initialize,
    send,
    clear,
    retry,
    recordInteraction,
    startPolling,
    stopPolling,
  };
}
